import { useCallback, useState } from 'react';
import { debounce } from 'lodash';
import { getWorkspaces } from '../../../api/workspaces';
import { WorkspaceOption } from '../models';

const PAGE_SIZE = 20;

export const useWorkspaceSearch = () => {
    const [options, setOptions] = useState<WorkspaceOption[]>([]);
    const [search, setSearch] = useState<string>('');
    const [page, setPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);

    const fetchPage = useCallback(async (nextPage: number, query: string) => {
        const { data } = await getWorkspaces({ paginator: { limit: PAGE_SIZE, page: nextPage }, search: query });
        const items: WorkspaceOption[] = (data?.data || []).map((w: any) => ({ _id: w._id, name: w.name }));
        return items;
    }, []);

    const onScroll = useCallback(async (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 32 && !loadingMore && hasMore) {
            setLoadingMore(true);
            const next = page + 1;
            const items = await fetchPage(next, search);
            setOptions((prev) => {
                const ids = new Set(prev.map((ws) => ws._id));
                const newItems = (items || []).filter((ws) => !ids.has(ws._id));
                return [...prev, ...newItems];
            });
            setHasMore((items?.length || 0) >= PAGE_SIZE);
            setPage(next);
            setLoadingMore(false);
        }
    }, [fetchPage, hasMore, loadingMore, page, search]);

    const debouncedSearch = useCallback(
        debounce(async (value: string) => {
            setSearch(value);
            setLoadingMore(true);
            const items = await fetchPage(1, value);
            setOptions(items);
            setHasMore((items?.length || 0) >= PAGE_SIZE);
            setPage(1);
            setLoadingMore(false);
        }, 600),
        [fetchPage],
    );

    const onSearch = useCallback((value: string) => {
        setOptions([]);
        debouncedSearch(value);
    }, [debouncedSearch]);

    const onDropdownVisibleChange = useCallback(async (open: boolean) => {
        if (!open) return;
        setLoadingMore(true);
        const items = await fetchPage(1, search);
        setOptions(items);
        setHasMore((items?.length || 0) >= PAGE_SIZE);
        setPage(1);
        setLoadingMore(false);
    }, [fetchPage, search]);

    return {
        options,
        search,
        hasMore,
        page,
        loadingMore,
        onSearch,
        onScroll,
        onDropdownVisibleChange,
        setOptions,
    };
};
